import { zodResolver } from '@hookform/resolvers/zod'
import { useGate, useStore } from 'effector-react'
import { useForm } from 'react-hook-form'
import { DEFAULT_REGION_OPTION } from '~/shared/config/constants'
import { snackbarEnqueued } from '~/shared/lib/notifications'
import { sanitizePhoneNumber } from '~/shared/lib/utils'
import { Button } from '~/shared/ui/Buttons'
import { useStorageRepeatCode } from '~/shared/ui/RepeatCode'
import { filterHelpers } from '~/shared/ui/ScrollableList'
import { YM_KEYS_ENUM, useEvents } from '~/shared/ui/YandexMetrika'
import {
  LoginStepsEnum,
  TIMER_REPEAT_LOGIN_CODE,
  TIMER_REPEAT_LOGIN_CODE_TIME,
} from './constants'
import {
  $loginCodeChannel,
  $loginStep,
  Gate,
  goToStepLoginCode,
  loginFx,
  requestLoginCodeFx,
} from './model'
import { StepLoginCode } from './StepLoginCode'
import { StepLoginPhone, stepLoginPhoneModel } from './StepLoginPhone'
import { RegistrationWrapper } from './styled'

export function Login() {
  useGate(Gate)
  const { ymEvent } = useEvents()

  const form = useForm<stepLoginPhoneModel.LoginPhoneFormValues>({
    resolver: zodResolver(stepLoginPhoneModel.loginPhoneSchema),
  })
  const phone = form.watch('phone')

  const repeatCodeStorageKey = `${TIMER_REPEAT_LOGIN_CODE}-${phone}`

  const loginStep = useStore($loginStep)
  const loginCodeChannel = useStore($loginCodeChannel)
  const { runTimer, getIsTimerWorking } =
    useStorageRepeatCode(repeatCodeStorageKey)

  const sanitizePhone = sanitizePhoneNumber(phone)

  const handleLogin = async (code: string) => {
    await loginFx({ phone: sanitizePhone, code })
  }

  const warningRequestCod = () => {
    snackbarEnqueued({
      message:
        'Запросите код повторно после того как пройдет блокировка получения кода',
      variant: 'warning',
    })
  }

  const requestLoginCode = async (channel?: 'sms') => {
    await requestLoginCodeFx({ phone: sanitizePhone, channel })
    runTimer(TIMER_REPEAT_LOGIN_CODE_TIME)
  }

  const handleCodeRequest = async (channel?: 'sms') => {
    if (getIsTimerWorking()) {
      warningRequestCod()
    } else {
      await requestLoginCode(channel)
    }

    goToStepLoginCode()
  }

  const handleSendSms = () => handleCodeRequest('sms')

  const handleCodeRepeat = () =>
    handleCodeRequest(loginCodeChannel?.channel === 'sms' ? 'sms' : undefined)

  const regionFilter = `regionCode=${filterHelpers.encodeOption(
    DEFAULT_REGION_OPTION,
  )}`

  return (
    <div>
      {loginStep === LoginStepsEnum.LOGIN_PHONE && (
        <>
          <StepLoginPhone form={form} onSuccess={handleCodeRequest} />

          <Button
            to={`/booking-cars?${regionFilter}`}
            fullWidth
            variant='lightSteelBlueBorder'
            style={{ margin: '24px 0' }}
          >
            Каталог автомобилей
          </Button>

          <RegistrationWrapper>
            <div>Нет аккаунта?</div>
            <Button
              to='/taxiparks'
              onClick={() => {
                ymEvent(YM_KEYS_ENUM.button_go_to_register)
              }}
              variant='textLink'
              contentProps={{ style: { padding: 0, marginLeft: '8px' } }}
              style={{ fontSize: '14px' }}
            >
              Зарегистрироваться
            </Button>
          </RegistrationWrapper>
        </>
      )}
      {loginStep === LoginStepsEnum.LOGIN_CODE && (
        <StepLoginCode
          onLogin={handleLogin}
          onCodeRepeat={handleCodeRepeat}
          onSendSms={handleSendSms}
          repeatCodeStorageKey={repeatCodeStorageKey}
          channel={loginCodeChannel?.channel}
          maskedEmail={loginCodeChannel?.maskedEmail ?? null}
        />
      )}
    </div>
  )
}
